import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface TemplateRow {
  readonly templateId: string;
  readonly templateCode: string;
  readonly templateName: string;
  readonly templateType: string;
  readonly templateCategory: string | null;
  readonly status: string;
  readonly isMarketplace: boolean;
  readonly installCount: number;
}

const TEMPLATE_TYPES = ['', 'FORM', 'WORKFLOW', 'REPORT', 'DASHBOARD', 'APPLICATION', 'WIDGET'];
const CATEGORIES = ['', 'VENDOR_REGISTRATION', 'SURVEY', 'AUDIT_CHECKLIST', 'APPROVAL_REQUEST', 'INSPECTION_FORM', 'COMPLIANCE_FORM', 'EXPENSE_CLAIM', 'ASSET_VERIFICATION', 'GENERAL'];
const STATUSES = ['', 'ACTIVE', 'INACTIVE', 'ARCHIVED'];
const CREATE_TYPES = TEMPLATE_TYPES.filter(Boolean);
const CREATE_CATEGORIES = CATEGORIES.filter(Boolean);

/**
 * M28 — Template Library (§21 / §22 / §32). Catalogue of reusable design assets
 * (form, workflow, report, dashboard, application, widget) across the eight
 * seeded categories plus GENERAL. Lists every template_library (T374) row with
 * its type, category, lifecycle status, marketplace-published flag and install
 * count, filterable by type, category and status. Supports create → publish to
 * marketplace → install → archive, each wired to the guarded /v1/lcnc/templates
 * write API.
 */
export function M28TemplateLibraryPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<TemplateRow[]>([]);
  const [templateType, setTemplateType] = useState('');
  const [templateCategory, setTemplateCategory] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [name, setName] = useState('');
  const [newType, setNewType] = useState('FORM');
  const [newCategory, setNewCategory] = useState('GENERAL');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (templateType) params.set('templateType', templateType);
    if (templateCategory) params.set('templateCategory', templateCategory);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: TemplateRow[] }>(`/v1/lcnc/templates${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, templateType, templateCategory, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m28.templateLibrary.notAuthorised', { verb, perm })
      : (err as Error).message;

  const runRowAction = async (fn: () => Promise<unknown>, successMsg: string, verb: string, perm: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(tr('m28.templateLibrary.validationName')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: TemplateRow }>(`/v1/lcnc/templates`, {
        method: 'POST',
        body: { templateName: name.trim(), templateType: newType, templateCategory: newCategory },
        token,
      });
      setNotice(tr('m28.templateLibrary.noticeCreated', { code: r.data.templateCode }));
      setName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.templateLibrary.verbManage'), 'TEMPLATE_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const publishToMarketplace = (t: TemplateRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/templates/${t.templateId}/marketplace`, { method: 'POST', body: {}, token }),
      tr('m28.templateLibrary.noticePublished', { code: t.templateCode }), tr('m28.templateLibrary.verbPublish'), 'MARKETPLACE_PUBLISH',
    );

  const install = (t: TemplateRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/templates/${t.templateId}/install`, { method: 'POST', body: {}, token }),
      tr('m28.templateLibrary.noticeInstalled', { code: t.templateCode }), tr('m28.templateLibrary.verbInstall'), 'TEMPLATE_VIEW',
    );

  const archive = (t: TemplateRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/templates/${t.templateId}/archive`, { method: 'POST', body: {}, token }),
      tr('m28.templateLibrary.noticeArchived', { code: t.templateCode }), tr('m28.templateLibrary.verbManage'), 'TEMPLATE_MANAGE',
    );

  return (
    <main aria-label="template-library">
      <h1>{tr('m28.templateLibrary.title')}</h1>

      <section aria-label="create-template">
        <h2>{tr('m28.templateLibrary.newHeading')}</h2>
        <form aria-label="template-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m28.templateLibrary.fieldName')}<input aria-label="field-template-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{tr('m28.templateLibrary.fieldType')}
            <select aria-label="field-template-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m28.templateLibrary.fieldCategory')}
            <select aria-label="field-template-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
              {CREATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-template" disabled={busy}>{busy ? tr('m28.templateLibrary.creating') : tr('m28.templateLibrary.create')}</button>
        </form>
        {actionError && <p role="alert" aria-label="template-error">{actionError}</p>}
        {notice && <p aria-label="template-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m28.templateLibrary.filterType')}
          <select aria-label="filter-template-type" value={templateType} onChange={(e) => setTemplateType(e.target.value)}>
            {TEMPLATE_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m28.templateLibrary.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m28.templateLibrary.filterCategory')}
          <select aria-label="filter-template-category" value={templateCategory} onChange={(e) => setTemplateCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || tr('m28.templateLibrary.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m28.templateLibrary.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m28.templateLibrary.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m28.templateLibrary.loading')}</p> : (
        <>
          <p aria-label="template-count">{tr('m28.templateLibrary.count', { count: rows.length })}</p>
          <table aria-label="template-table">
            <thead><tr><th>{tr('m28.templateLibrary.colCode')}</th><th>{tr('m28.templateLibrary.colName')}</th><th>{tr('m28.templateLibrary.colType')}</th><th>{tr('m28.templateLibrary.colCategory')}</th><th>{tr('m28.templateLibrary.colStatus')}</th><th>{tr('m28.templateLibrary.colMarketplace')}</th><th>{tr('m28.templateLibrary.colInstalls')}</th><th>{tr('m28.templateLibrary.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.templateId}>
                  <td>{t.templateCode}</td><td>{t.templateName}</td><td>{t.templateType}</td>
                  <td>{t.templateCategory ?? '—'}</td><td>{t.status}</td>
                  <td>{t.isMarketplace ? tr('m28.templateLibrary.published') : '—'}</td><td>{t.installCount}</td>
                  <td>
                    <button type="button" aria-label={`marketplace-${t.templateCode}`} onClick={() => void publishToMarketplace(t)}>{tr('m28.templateLibrary.btnPublish')}</button>
                    <button type="button" aria-label={`install-${t.templateCode}`} onClick={() => void install(t)}>{tr('m28.templateLibrary.btnInstall')}</button>
                    <button type="button" aria-label={`archive-${t.templateCode}`} onClick={() => void archive(t)}>{tr('m28.templateLibrary.btnArchive')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
